import { frameLayout } from './fit.js';
import { cellOutline } from './hit-test.js';
import { byOrder, quadToStage } from './layout.js';
/**
 * 文言 1 文字ぶんの見積もり幅(表示ピクセル)。**全角で見る。**
 *
 * 実際の幅はフォントと字種で変わるが、**測るには DOM に置いて計るしかない** ——
 * 輪郭を組むたびにそれをやると重い。**安全側(全角)に倒して見積もる。**
 */
const HINT_CHAR_WIDTH = 13;
/** 文言の左右に残す余白。枠線ぎりぎりに文字が来ないようにする。 */
const HINT_PADDING = 16;
/** 文言を置くのに要る高さ。1 行ぶん + 上下の余白。 */
const HINT_MIN_HEIGHT = 32;
/** 多角形の外接矩形。 */
function boundingBox(points) {
    const xs = points.map(([x]) => x);
    const ys = points.map(([, y]) => y);
    return {
        width: Math.max(...xs) - Math.min(...xs),
        height: Math.max(...ys) - Math.min(...ys),
    };
}
/**
 * このセルに `text` が入るか。**入らなければ枠だけ描く。**
 *
 * ## なぜ文言を受け取るのか
 *
 * **固定の閾値にすると、文言を変えたときに黙って溢れる。** 実際、最初は
 * 「120px 以上なら出す」と決め打っていたが、**文言が 13px × 10 文字 = 130px**
 * で**閾値のほうが狭かった** —— 判定を通ったセルで文字がはみ出す。
 *
 * **長さを見れば、文言を変えても判定がついてくる。**
 *
 * ## なぜ外接矩形で測るのか
 *
 * セルは台形になりうるので「幅」「高さ」が一意に決まらない ——
 * 外接矩形なら**どんな形でも同じ尺度**で測れる。**安全側ではない**
 * (台形の狭いほうでは溢れうる)が、そこまで厳密にすると
 * ほとんどのセルで文言が出なくなる。
 */
export function fitsHint(points, text) {
    if (points.length === 0 || text === '')
        return false;
    const box = boundingBox(points);
    return box.width >= text.length * HINT_CHAR_WIDTH + HINT_PADDING && box.height >= HINT_MIN_HEIGHT;
}
/**
 * 多角形の**面積の重心**。
 *
 * **頂点の平均ではない。** 台形のセル(遠近のある写真)では、頂点の平均は
 * **狭いほうの辺に引っ張られる** —— 面積の重心のほうが「真ん中に見える」
 * 位置になる。
 *
 * 面積が 0(潰れた多角形)なら頂点の平均に落とす。
 */
export function centroidOf(points) {
    const average = () => [
        points.reduce((sum, [x]) => sum + x, 0) / points.length,
        points.reduce((sum, [, y]) => sum + y, 0) / points.length,
    ];
    if (points.length < 3)
        return average();
    let twiceArea = 0;
    let x = 0;
    let y = 0;
    for (let i = 0; i < points.length; i += 1) {
        const [x0, y0] = points[i] ?? [0, 0];
        const [x1, y1] = points[(i + 1) % points.length] ?? [0, 0];
        const cross = x0 * y1 - x1 * y0;
        twiceArea += cross;
        x += (x0 + x1) * cross;
        y += (y0 + y1) * cross;
    }
    if (twiceArea === 0)
        return average();
    return [x / (3 * twiceArea), y / (3 * twiceArea)];
}
/**
 * 空のセルの輪郭を、**重なり順**(`order` が小さいほど先)で返す。
 *
 * `hasSource` は「そのセルに素材が載っているか」を答える。**描画コアの台帳
 * (`upload-registry`)が知っている**ので、呼び出し側から状態を渡す必要はない。
 *
 * **潰れたクアッドのセルは飛ばす。** 描けないものを描こうとしない。
 */
export function emptyCellOutlines(frames, transform, hasSource) {
    const shapes = [];
    for (const frame of byOrder(frames)) {
        const stageQuad = quadToStage(frame.quad, transform);
        // **未分割の枠はセル 1 つ**として返る(`fit.ts` の `frameLayout`)。
        const layout = frameLayout(stageQuad, frame.grid);
        if (layout === null)
            continue;
        for (const cell of layout.cells) {
            if (hasSource(frame.id, cell.index))
                continue;
            const points = cellOutline(stageQuad, frame.grid, cell.index);
            if (points === null)
                continue;
            shapes.push({ frameId: frame.id, cellIndex: cell.index, points });
        }
    }
    return shapes;
}
//# sourceMappingURL=outlines.js.map